import {
    StoryPreviewFragment,
    StoryToPlayFragment,
} from '@graphql/generated/graphql';
import {
    createContext,
    FC,
    PropsWithChildren,
    useContext,
    useState,
    useCallback,
} from 'react';

import { SanitizedStoryPage } from '../types';

interface StoryContext {
    module?: string;
    story?: StoryToPlayFragment;
    storyPages: SanitizedStoryPage[];
    previousStory?: StoryPreviewFragment;
    nextStory?: StoryPreviewFragment;
    removePage: (pageIndex: number) => void;
    getActiveStoryPages: () => SanitizedStoryPage[];
}

interface StoryProviderProps
    extends Omit<StoryContext, 'removePage' | 'getActiveStoryPages'> {
    children: React.ReactNode;
}

const StoryContext = createContext<StoryContext>({
    module: undefined,
    story: undefined,
    storyPages: [],
    previousStory: undefined,
    nextStory: undefined,
    removePage: () => {},
    getActiveStoryPages: () => [],
});

export const StoryProvider: FC<PropsWithChildren<StoryProviderProps>> = ({
    children,
    module,
    story,
    storyPages,
    previousStory,
    nextStory,
}) => {
    const [removedPageIndices, setRemovedPageIndices] = useState<Set<number>>(
        new Set()
    );

    const removePage = useCallback((pageIndex: number) => {
        setRemovedPageIndices((prev) => {
            const newSet = new Set(prev);
            newSet.add(pageIndex);
            return newSet;
        });
    }, []);

    const getActiveStoryPages = useCallback(() => {
        return storyPages.filter((_, index) => !removedPageIndices.has(index));
    }, [storyPages, removedPageIndices]);

    return (
        <StoryContext.Provider
            value={{
                module,
                story,
                storyPages,
                previousStory,
                nextStory,
                removePage,
                getActiveStoryPages,
            }}
        >
            {children}
        </StoryContext.Provider>
    );
};

export const useStoryContext = () => {
    const context = useContext(StoryContext);
    if (!context) {
        throw new Error('useStoryContext must be used within a StoryProvider');
    }
    return context;
};
